/** Inline banners: an error, a warning, a note.
 *
 * Every tone resolves through the status tokens, so a freeze warning is the same
 * amber as an amber SLA and a failed mutation is the same red as a breach.
 */
import { AlertTriangle, Info, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '../../lib/utils'

type Tone = 'critical' | 'warning' | 'info'

const TONES: Record<Tone, { wrapper: string; icon: typeof Info }> = {
  critical: { wrapper: 'border-critical/30 bg-critical/10 text-critical', icon: XCircle },
  warning: { wrapper: 'border-warning/30 bg-warning/10 text-warning', icon: AlertTriangle },
  info: { wrapper: 'border-info/30 bg-info/10 text-info', icon: Info },
}

export function Callout({
  tone = 'info',
  children,
  className,
}: {
  tone?: Tone
  children: ReactNode
  className?: string
}) {
  const { wrapper, icon: Icon } = TONES[tone]
  return (
    <div
      role={tone === 'critical' ? 'alert' : undefined}
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
        wrapper,
        className,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0">{children}</div>
    </div>
  )
}

/** The failed-mutation banner. Kept as its own name because it is used in a
 *  dozen places and "error" is the only tone that ever made sense there. */
export function ErrorNote({ message, className }: { message: string; className?: string }) {
  return (
    <Callout tone="critical" className={className}>
      {message}
    </Callout>
  )
}
